import { useState, useEffect, useMemo } from 'react';
import { Search, Bell, BellOff, Loader2, Users, ShoppingBag, X, FolderHeart, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Link, useNavigate } from 'react-router-dom';
import { WaveButton } from '@/components/users/WaveButton';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { getAvatarUrl } from '@/lib/avatars';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { OptimizedImage } from '@/components/ui/OptimizedImage';
import { useSemanticSearch, SearchMatch } from '@/hooks/useSemanticSearch';

interface UserProfile {
  user_id: string;
  display_name: string | null;
  avatar_url: string | null;
  bio: string | null;
}

interface SearchSavedItem {
  id: string;
  name: string | null;
  image_url: string | null;
  store: string | null;
  price: number | null;
  currency: string | null;
  ai_summary: string | null;
  ai_category: string | null;
  ai_tags: string[] | null;
}

interface CollectionResult {
  id: string;
  name: string;
  cover_image_url: string | null;
  color: string | null;
  is_public: boolean;
  is_collaborative: boolean;
  item_count: number;
  owner_name: string | null;
  owner_avatar: string | null;
}

interface UniversalSearchSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function UniversalSearchSheet({ open, onOpenChange }: UniversalSearchSheetProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  // Removed activeTab - now showing unified results
  
  // Users state
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [followingIds, setFollowingIds] = useState<Set<string>>(new Set());
  const [followLoadingId, setFollowLoadingId] = useState<string | null>(null);
  
  // Products state
  const [products, setProducts] = useState<SearchSavedItem[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  
  // Semantic search
  const { matches: semanticMatches, isSearching: semanticSearching, search: runSemanticSearch, clearSearch } = useSemanticSearch(products);

  // Collections state
  const [collections, setCollections] = useState<CollectionResult[]>([]);
  const [collectionsLoading, setCollectionsLoading] = useState(false);

  // Reset search when sheet closes
  useEffect(() => {
    if (!open) {
      setSearchQuery('');
      clearSearch();
    }
  }, [open, clearSearch]);

  // Fetch users the current user is following
  useEffect(() => {
    if (!user || !open) return;

    const fetchFollowing = async () => {
      const { data } = await supabase
        .from('follows')
        .select('following_id')
        .eq('follower_id', user.id);
      
      if (data) {
        setFollowingIds(new Set(data.map(f => f.following_id)));
      }
    };

    fetchFollowing();
  }, [user, open]);

  // Search ALL categories when query changes (unified search)
  useEffect(() => {
    if (!open) return;

    const searchAll = async () => {
      // Search users
      setUsersLoading(true);
      try {
        let query = supabase
          .from('profiles')
          .select('user_id, display_name, avatar_url, bio')
          .order('created_at', { ascending: false })
          .limit(20);

        if (searchQuery.trim()) {
          query = query.ilike('display_name', `%${searchQuery.trim()}%`);
        }

        if (user) {
          query = query.neq('user_id', user.id);
        }

        const { data, error } = await query;
        if (!error) setUsers(data || []);
      } catch (error) {
        console.error('Error searching users:', error);
      } finally {
        setUsersLoading(false);
      }

      // Fetch products for semantic search (only if logged in)
      if (user) {
        setProductsLoading(true);
        try {
          // Fetch all user's products for semantic search
          // AI fields come from page_ai_data (URL items) or saved_items columns (image items)
          const { data: rawData, error } = await supabase
            .from('saved_items')
            .select(`id, name, image_url, store, price, currency, ai_summary, ai_category, ai_tags, page_id,
              flux_pages:page_id(url, tld, data,
                page_ai_data(ai_summary, ai_category, ai_tags)
              )`)
            .eq('user_id', user.id)
            .order('created_at', { ascending: false })
            .limit(100);

          if (!error && rawData) {
            const data = rawData.map((row: any) => {
              const fp = row.flux_pages;
              const d = fp?.data;
              const pageAi = fp?.page_ai_data;
              const hasFluxData = row.page_id && d && typeof d === 'object';
              const aiSource = pageAi || row;
              return {
                id: row.id,
                name: hasFluxData ? (d.title || row.name) : (row.name ?? null),
                image_url: hasFluxData ? (d.cdnUrls?.[0] || d.imageUrls?.[0] || row.image_url) : (row.image_url ?? null),
                store: hasFluxData ? (fp.tld || d.brand || row.store) : (row.store ?? null),
                price: hasFluxData ? (d.price != null ? parseFloat(String(d.price)) : (row.price ?? null)) : (row.price ?? null),
                currency: hasFluxData ? (d.currency || row.currency || 'USD') : (row.currency ?? 'USD'),
                ai_summary: aiSource?.ai_summary ?? null,
                ai_category: aiSource?.ai_category ?? null,
                ai_tags: aiSource?.ai_tags ?? null,
              };
            });
            setProducts(data);
            
            // Trigger semantic search if there's a query
            if (searchQuery.trim().length >= 2) {
              runSemanticSearch(searchQuery.trim());
            } else {
              clearSearch();
            }
          }
        } catch (error) {
          console.error('Error fetching products:', error);
        } finally {
          setProductsLoading(false);
        }
      }

      // Search collections
      setCollectionsLoading(true);
      try {
        let query = supabase
          .from('collections')
          .select(`id, name, cover_image_url, color, is_public, is_collaborative, user_id`)
          .order('created_at', { ascending: false })
          .limit(30);

        if (searchQuery.trim()) {
          query = query.ilike('name', `%${searchQuery.trim()}%`);
        }

        if (user) {
          query = query.or(`is_public.eq.true,user_id.eq.${user.id}`);
        } else {
          query = query.eq('is_public', true);
        }

        const { data: collectionsData, error } = await query;

        if (!error && collectionsData) {
          const collectionsWithCounts = await Promise.all(
            collectionsData.map(async (collection) => {
              const { count } = await supabase
                .from('collection_items')
                .select('*', { count: 'exact', head: true })
                .eq('collection_id', collection.id);

              const { data: ownerData } = await supabase
                .from('profiles')
                .select('display_name, avatar_url')
                .eq('user_id', collection.user_id)
                .single();

              return {
                id: collection.id,
                name: collection.name,
                cover_image_url: collection.cover_image_url,
                color: collection.color || null,
                is_public: collection.is_public,
                is_collaborative: collection.is_collaborative,
                item_count: count || 0,
                owner_name: ownerData?.display_name || null,
                owner_avatar: ownerData?.avatar_url || null,
              };
            })
          );
          setCollections(collectionsWithCounts);
        }
      } catch (error) {
        console.error('Error searching collections:', error);
      } finally {
        setCollectionsLoading(false);
      }
    };

    const debounce = setTimeout(searchAll, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery, open, user]);

  const toggleFollow = async (targetUserId: string) => {
    if (!user) return;

    setFollowLoadingId(targetUserId);
    try {
      const isFollowing = followingIds.has(targetUserId);
      
      if (isFollowing) {
        await supabase
          .from('follows')
          .delete()
          .eq('follower_id', user.id)
          .eq('following_id', targetUserId);
        
        setFollowingIds(prev => {
          const next = new Set(prev);
          next.delete(targetUserId);
          return next;
        });
      } else {
        await supabase
          .from('follows')
          .insert({
            follower_id: user.id,
            following_id: targetUserId,
          });
        
        setFollowingIds(prev => new Set([...prev, targetUserId]));
      }
    } catch (error) {
      console.error('Error toggling follow:', error);
    } finally {
      setFollowLoadingId(null);
    }
  };

  const formatPrice = (price: number | null, currency: string | null) => {
    if (price === null) return null;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:w-96 p-0 flex flex-col">
        <SheetHeader className="px-5 pb-4 border-b border-border/30" style={{ paddingTop: 'calc(1.5rem + env(safe-area-inset-top))' }}>
          <SheetTitle className="flex items-center gap-2">
            <Search className="w-5 h-5 text-primary" />
            Search
          </SheetTitle>
        </SheetHeader>

        <div className="px-5 py-4 border-b border-border/30 space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search users, saves & collections..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-9"
              autoFocus
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-3 py-2 space-y-4">
          {/* Loading state */}
          {(usersLoading || productsLoading || collectionsLoading) && users.length === 0 && products.length === 0 && collections.length === 0 && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          )}

          {/* Empty state */}
          {!usersLoading && !productsLoading && !collectionsLoading && 
           users.length === 0 && products.length === 0 && collections.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                <Search className="w-8 h-8 text-muted-foreground" />
              </div>
              <p className="text-muted-foreground text-sm">
                {searchQuery ? 'No results found' : 'Start typing to search'}
              </p>
            </div>
          )}

          {/* Users section */}
          {users.length > 0 && (
            <div>
              <div className="flex items-center gap-2 px-2 mb-2">
                <Users className="w-4 h-4 text-muted-foreground" />
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">People</span>
              </div>
              <div className="space-y-1">
                {users.slice(0, 5).map((profile) => {
                  const isFollowing = followingIds.has(profile.user_id);
                  const isLoading = followLoadingId === profile.user_id;
                  
                  return (
                    <div
                      key={profile.user_id}
                      className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-muted/50 transition-colors"
                    >
                      <Link 
                        to={`/user/${profile.user_id}`}
                        onClick={() => onOpenChange(false)}
                      >
                        <Avatar className="w-10 h-10 ring-2 ring-border/30">
                          <AvatarImage src={getAvatarUrl(profile.avatar_url, profile.user_id)} />
                          <AvatarFallback className="bg-muted" />
                        </Avatar>
                      </Link>
                      
                      <div className="flex-1 min-w-0">
                        <Link 
                          to={`/user/${profile.user_id}`}
                          onClick={() => onOpenChange(false)}
                          className="hover:underline"
                        >
                          <p className="font-medium text-sm text-foreground truncate">
                            {profile.display_name || 'Anonymous'}
                          </p>
                        </Link>
                        {profile.bio && (
                          <p className="text-xs text-muted-foreground truncate">
                            {profile.bio}
                          </p>
                        )}
                      </div>
                      
                      {user && (
                        <div className="flex items-center gap-1">
                          <WaveButton 
                            recipientId={profile.user_id} 
                            recipientName={profile.display_name || undefined}
                            size="sm"
                          />
                          <Button
                            onClick={() => toggleFollow(profile.user_id)}
                            disabled={isLoading}
                            variant={isFollowing ? "outline" : "default"}
                            size="sm"
                            className={cn(
                              "gap-1 shrink-0 h-8 px-3",
                              isFollowing && "border-muted-foreground/30 text-muted-foreground"
                            )}
                          >
                            {isLoading ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : isFollowing ? (
                              <BellOff className="w-3.5 h-3.5" />
                            ) : (
                              <Bell className="w-3.5 h-3.5" />
                            )}
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Saves section - Semantic AI Search Results */}
          {user && searchQuery.trim().length >= 2 && (semanticSearching || semanticMatches.length > 0) && (
            <div>
              <div className="flex items-center gap-2 px-2 mb-2">
                <Sparkles className="w-4 h-4 text-primary" />
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  AI Matches
                </span>
                {semanticSearching && (
                  <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />
                )}
              </div>
              <div className="grid grid-cols-2 gap-2">
                {semanticMatches.slice(0, 6).map((match) => {
                  const item = products.find(p => p.id === match.id);
                  if (!item) return null;
                  
                  return (
                    <Link
                      key={item.id}
                      to={`/saved/${item.id}`}
                      onClick={() => onOpenChange(false)}
                      className="block rounded-xl overflow-hidden bg-card border border-border/50 hover:ring-2 hover:ring-primary/50 transition-all"
                    >
                      <div className="aspect-square relative">
                        {item.image_url ? (
                          <OptimizedImage
                            src={item.image_url}
                            alt={item.name || 'Saved item'}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-muted flex items-center justify-center">
                            <ShoppingBag className="w-8 h-8 text-muted-foreground" />
                          </div>
                        )}
                        {item.price && (
                          <div className="absolute bottom-1.5 left-1.5 bg-background/90 backdrop-blur-sm rounded-full px-2 py-0.5">
                            <span className="text-xs font-semibold">{formatPrice(item.price, item.currency)}</span>
                          </div>
                        )}
                      </div>
                      <div className="p-2">
                        <p className="text-xs text-muted-foreground truncate">{item.store || 'Saved'}</p>
                        <p className="text-sm font-medium truncate">{item.name || 'Untitled'}</p>
                        {match.reason && (
                          <p className="text-[10px] text-primary/80 truncate mt-0.5 flex items-center gap-1">
                            <Sparkles className="w-2.5 h-2.5 shrink-0" />
                            {match.reason}
                          </p>
                        )}
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {/* Saves section - Default (no search query) */}
          {user && products.length > 0 && searchQuery.trim().length < 2 && (
            <div>
              <div className="flex items-center gap-2 px-2 mb-2">
                <ShoppingBag className="w-4 h-4 text-muted-foreground" />
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Your Saves</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {products.slice(0, 6).map((item) => (
                  <Link
                    key={item.id}
                    to={`/saved/${item.id}`}
                    onClick={() => onOpenChange(false)}
                    className="block rounded-xl overflow-hidden bg-card border border-border/50 hover:ring-2 hover:ring-primary/50 transition-all"
                  >
                    <div className="aspect-square relative">
                      {item.image_url ? (
                        <OptimizedImage
                          src={item.image_url}
                          alt={item.name || 'Saved item'}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-muted flex items-center justify-center">
                          <ShoppingBag className="w-8 h-8 text-muted-foreground" />
                        </div>
                      )}
                      {item.price && (
                        <div className="absolute bottom-1.5 left-1.5 bg-background/90 backdrop-blur-sm rounded-full px-2 py-0.5">
                          <span className="text-xs font-semibold">{formatPrice(item.price, item.currency)}</span>
                        </div>
                      )}
                    </div>
                    <div className="p-2">
                      <p className="text-xs text-muted-foreground truncate">{item.store || 'Saved'}</p>
                      <p className="text-sm font-medium truncate">{item.name || 'Untitled'}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Collections section */}
          {collections.length > 0 && (
            <div>
              <div className="flex items-center gap-2 px-2 mb-2">
                <FolderHeart className="w-4 h-4 text-muted-foreground" />
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Collections</span>
              </div>
              <div className="space-y-1">
                {collections.slice(0, 5).map((collection) => {
                  // Resolve collection color to proper CSS value
                  const colorMap: Record<string, string> = {
                    primary: '#F24316', red: '#EF4444', blue: '#3B82F6',
                    green: '#22C55E', purple: '#A855F7', pink: '#EC4899',
                    yellow: '#EAB308', orange: '#F97316', teal: '#14B8A6', indigo: '#6366F1',
                  };
                  const resolvedColor = collection.color 
                    ? (colorMap[collection.color] || collection.color)
                    : null;

                  return (
                    <button
                      key={collection.id}
                      onClick={() => {
                        onOpenChange(false);
                        navigate(`/collection/${collection.id}`);
                      }}
                      className="w-full flex items-center gap-3 p-2.5 rounded-xl hover:bg-muted/50 transition-colors text-left"
                    >
                      <div 
                        className="w-12 h-12 rounded-lg overflow-hidden shrink-0"
                        style={!collection.cover_image_url && resolvedColor 
                          ? { background: `linear-gradient(135deg, ${resolvedColor}66, ${resolvedColor}33)` }
                          : !collection.cover_image_url 
                            ? { background: 'hsl(var(--muted))' }
                            : undefined
                        }
                      >
                        {collection.cover_image_url ? (
                          <OptimizedImage
                            src={collection.cover_image_url}
                            alt={collection.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <FolderHeart className="w-5 h-5 text-foreground/60" />
                          </div>
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className="font-medium text-sm text-foreground truncate">
                            {collection.name}
                          </p>
                          {collection.is_public && (
                            <span className="shrink-0 text-[10px] font-medium text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30 px-1.5 py-0.5 rounded-full">
                              Public
                            </span>
                          )}
                          {collection.is_collaborative && (
                            <span className="shrink-0 text-[10px] font-medium text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/30 px-1.5 py-0.5 rounded-full">
                              Group
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground truncate">
                          {collection.owner_name || 'Unknown'} · {collection.item_count} item{collection.item_count !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
